import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import forEach from 'lodash/forEach';
import isEqual from 'lodash/isEqual';
import keys from 'lodash/keys';
import find from 'lodash/find';
import _ from 'lodash';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core';

import * as Constants from '../../../../../constants/charts';
import { AGGREGATED_DATA_TYPE } from '../../../../../constants/metrics';
import { getI18n } from "../../../../../utils/intl";

import Loading from "../../../../Loading";
import CompositeXYChart from '../../../CompositeXYChart';
import NoData from '../../../NoData';
import ChartLegend from '../../../ChartLegend';
import CustomLabelComponent from '../../../CustomLabelComponent';

import {
  getDateRangeData, getDisplayDate,
  getParamsFromQueryData, lineColors, lightShadeLineColors, getParamsForHistoricalData,
  clearHistoricalDataMutations,
  clearMetricsHoverState,
} from '../../../utils';
import {
  getEntityPermuteMap,
  getBucketPermuteIdAndNames,
  getSourcesIdsFromResponse,
  getDefaultSharedMutations,
  getMetricKey,
  getSourceFromMetricKey,
  getMetricFromMetricKey,
} from '../../../utils/permute';
import { getXValueTick } from '../../../utils/filter';

import { TooltipStyle } from '../../../ChartTooltip/index.theme';
import styles from './styles';

const LATENCY_METRIC_AVG = 'avg';
const LATENCY_METRIC_MIN = 'min';
const LATENCY_METRIC_MAX = 'max';
const LATENCY_METRIC_CUSTOM_DATE = 'customDate';
const LATENCY_METRIC_CUSTOM_WEEK = 'customWeek';

const LatencyChart = (props) => {
  const {
    isLoading,
    defaultLatencyResponse,
    fetchCustomDateMetricsData,
    is30DaysDataLoading,
    is52WeeksDataLoading,
    latencyResponseFor30DaysAgo,
    latencyResponseFor52WeeksAgo,
    apiMap, appMap, orgMap,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    sharedMutations: {},
    keyMetricDataTS: 0,
    key: '',
    metricEvent: '',
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  const defaultLatencyResponseRef = useRef(defaultLatencyResponse);
  useEffect(() => {
    const entitiesMap = { apiMap, appMap, orgMap };
    const oldSourcesIds = (state.sharedMutations && keys(state.sharedMutations)) || [];
    const newSourcesIds = getSourcesIdsFromResponse(defaultLatencyResponse, 'latencies', entitiesMap);

    const hasNewSourceIds = newSourcesIds.length > 0;
    // reset sharedMutations if sources changes
    if (hasNewSourceIds && !isEqual(oldSourcesIds.sort(), newSourcesIds.sort())) {
      const sharedMutations = getDefaultSharedMutations(newSourcesIds, LATENCY_METRIC_AVG);
      setState({
        sharedMutations,
        keyMetricDataTS: defaultLatencyResponse?.timeStamp ?? 0,
      });
    } else if (hasNewSourceIds && defaultLatencyResponse.timeStamp > state.keyMetricDataTS) {
      setState({
        sharedMutations: clearHistoricalDataMutations(state.sharedMutations),
        keyMetricDataTS: defaultLatencyResponse?.timeStamp ?? 0,
      });
    }
    defaultLatencyResponseRef.current = defaultLatencyResponse;
  }, [defaultLatencyResponse]);

  useEffect(() => {
    setState(
      prevState => {
        const { key, metricEvent, sharedMutations= {} } = prevState
        const sourceKey = metricEvent ? getSourceFromMetricKey(key) : key;
        const metricKey = getMetricFromMetricKey(key);
        const sourceMutation = (sharedMutations && sharedMutations[sourceKey]) || {};
        const metricsMutation = sourceMutation.metrics || {};
        const metricMutation = metricsMutation[metricKey];
        const defaultResponse = defaultLatencyResponse;
        if (defaultResponse && defaultResponse.query) {
          if ((metricKey === LATENCY_METRIC_CUSTOM_DATE) && metricMutation.show) {
            const params = getParamsForHistoricalData(
              defaultResponse.query,
              Constants.HISTORICAL_METRIC_30DAYS,
              defaultResponse.data.latencies,
            );
            fetchCustomDateMetricsData(params);
          }
          if ((metricKey === LATENCY_METRIC_CUSTOM_WEEK) && metricMutation.show) {
            const params = getParamsForHistoricalData(
              defaultResponse.query,
              Constants.HISTORICAL_METRIC_52WEEKS,
              defaultResponse.data.latencies,
            );
            fetchCustomDateMetricsData(params);
          }
        }
      },
    );
  }, [state.key, state.metricEvent]);

  const getHistoricalChartDataFromResponse = (latencyResponse) => {
    const queryParams = getParamsFromQueryData(
      latencyResponse && latencyResponse.query,
    );
    const timerange = queryParams.timerange;
    const timeAgg = queryParams.timeAgg;
    const startDate = queryParams.startDate;
    const endDate = queryParams.endDate;
    // get dateRangeData passed, instead of calculating it
    const dateRangeData = getDateRangeData(
      timerange,
      timeAgg,
      startDate,
      endDate,
    );
    const historicalChartDataFromResponse = getLatencyChartData(
      dateRangeData,
      latencyResponse,
    );
    return historicalChartDataFromResponse;
  }
  
  const getLatencyChartData = (xAxisData, latencyResponse) => {
    // This is a special check, even when there are no transactions for a given
    // duration, backend sends an entry for each date under latenciesTs. If there
    // is a fix from backend, then this check needs to be removed.
    if (latencyResponse && latencyResponse.data &&
      !_.isUndefined(latencyResponse.data.latencies) &&
      _.isEmpty(latencyResponse.data.latencies)) {
      return [];
    }
    const {
      timerange,
      timeAgg,
      startDate,
      endDate,
    } = getParamsFromQueryData(latencyResponse && latencyResponse.query);
    const permuteLatenciesTs =
      latencyResponse && latencyResponse.data && latencyResponse.data.latenciesTs;
    const sortedPermuteLatenciesTs =
      (permuteLatenciesTs &&
        permuteLatenciesTs.sort((a, b) => (a.date > b.date ? 1 : -1))) ||
      [];
    let latenciesBuckets = (latencyResponse && latencyResponse.data &&
      latencyResponse.data.latencies && latencyResponse.data.latencies.buckets) || [];
    const entitiesStr = (latencyResponse && latencyResponse.query.buckets) || '';
    const permuteMap = getEntityPermuteMap(latenciesBuckets, entitiesStr, {
      apiMap,
      appMap,
      orgMap,
    });
    const chartData = [];
    if (!entitiesStr) {
      latenciesBuckets = keys(permuteMap);
    }
    latenciesBuckets.forEach((latencyBucket, index) => {
      const bucketPermuteObject = getBucketPermuteIdAndNames(latencyBucket, entitiesStr, {
        apiMap,
        appMap,
        orgMap,
      });
      const permuteId = bucketPermuteObject.permuteId;
      const data = [];
      let permuteLatencyBucket = {};
      sortedPermuteLatenciesTs.forEach(timeStampedData => {
        permuteLatencyBucket = find(timeStampedData.buckets, bucket => {
          const permuteObject = getBucketPermuteIdAndNames(bucket, entitiesStr, {
            apiMap,
            appMap,
            orgMap,
          });
          return permuteObject.permuteId === permuteId;
        });
        if (!entitiesStr) {
          permuteLatencyBucket = timeStampedData;
        }
        const currentPermuteObject = permuteMap[permuteId];
        const dateTimeStr =
          getDisplayDate(moment.utc(timeStampedData.date).toISOString(),
            timerange, timeAgg, startDate, endDate, 'tooltip');
        const avgValue = (permuteLatencyBucket && permuteLatencyBucket[LATENCY_METRIC_AVG]) || 0;
        const maxValue = (permuteLatencyBucket && permuteLatencyBucket[LATENCY_METRIC_MAX]) || 0;
        const minValue = (permuteLatencyBucket && permuteLatencyBucket[LATENCY_METRIC_MIN]) || 0;
        const labelValue = avgValue ?
          `${avgValue.toFixed(2)}ms` : getI18nStrings('noDataText');
        data.push({
          x: xAxisData && getXValueTick(xAxisData.xAxisValuesTickMap, timeStampedData.date),
          y: avgValue,
          label: `${currentPermuteObject.names.join(' > ')}\n\n${getI18nStrings('avgLatencyText')} : ${labelValue} \n${getI18nStrings('dateTimeFormat')} \n${dateTimeStr}`,
          [LATENCY_METRIC_MAX]: maxValue,
          [LATENCY_METRIC_MIN]: minValue,
          date: timeStampedData.date,
        });
      });
      // This check is in case of aggregated latency data, just in this case legend
      // has to pick avg, max, min from latencies under response data
      const actualLatencyBucket = (latencyResponse && latencyResponse.data &&
        latencyResponse.data.latencies) && permuteId === AGGREGATED_DATA_TYPE ?
        latencyResponse.data.latencies : latencyBucket;
      if (data.length) {
        chartData.push({
          customLabelComponent: createCustomLabelComponent(TooltipStyle),
          ...actualLatencyBucket,
          id: permuteId,
          names: permuteMap[permuteId].names,
          data,
          colorIndex: index,
        });
      }
    });
    return chartData;
  };

  const getXAxisFormatter = (item, dateRangeData) => {
    const xAxisTickValuesMap = dateRangeData.xAxisTickValuesMap;
    if (xAxisTickValuesMap[item]) {
      return getDisplayDate(xAxisTickValuesMap[item], dateRangeData.dateRange,
        dateRangeData.timeAgg, dateRangeData.startDate, dateRangeData.endDate);
    }
    return '';
  };

  const getI18nStrings = (key) => {
    let i18nString = '';
    if (!_.isUndefined(key)) {
      const strObj = {
        dateTimeFormat: 'label.analytics.date.time.format.text',
        noDataText: 'label.analytics.dashboard.noData.text',
        avgLatencyText: 'label.analytics.latencyChart.apiLatency.text',
        maxLatencyText: 'label.analytics.latencyChart.apiLatency.max.text',
        minLatencyText: 'label.analytics.latencyChart.apiLatency.min.text',
        yAxisLabel: 'label.analytics.api.latency.ms.text',
      };
      i18nString = intl.getI18nMessage(strObj[key]);
    }
    return i18nString;
  };

  const getLineChartsData = (latencyChartData, metric) => {
    const lineCharts = [];
    forEach(latencyChartData, item => {
      const { id, data, customLabelComponent } = item;
      let { colorIndex } = item;
      let pattern = '';
      let useLightShadedColor = false;
      const lineData = [];
      const mutations = state.sharedMutations;
      const sourceMutation = mutations[id] || {};
      const metricsMutation = sourceMutation.metrics || {};
      const avgMutation = metricsMutation[LATENCY_METRIC_AVG] || {};
      if (sourceMutation && sourceMutation.colorIndex !== undefined) {
        colorIndex = sourceMutation.colorIndex;
      }
      if (metric === LATENCY_METRIC_CUSTOM_DATE) {
        pattern = 'dashed';
      }
      if (metric === LATENCY_METRIC_CUSTOM_WEEK) {
        useLightShadedColor = true;
      }
      const metricMutation = metricsMutation[metric] || {};
      const highLight = !!metricMutation.hover;
      if ((!sourceMutation.show && !avgMutation.show) || !metricMutation.show) {
        return;
      }
      forEach(data, dataItem => {
        const { x, y, label } = dataItem;
        lineData.push({
          label,
          x,
          y,
        });
      });
      lineCharts.push({
        colorIndex,
        customLabelComponent,
        data: lineData,
        pattern,
        highLight,
        useLightShadedColor,
        id,
        metric,
        type: Constants.CHART_ELEMENT_LINE,
      });
    });
    return lineCharts;
  };

  const getAreaChartsData = (latencyChartData) => {
    const areaCharts = [];
    const {
      timerange,
      timeAgg,
      startDate,
      endDate,
    } = getParamsFromQueryData(defaultLatencyResponse && defaultLatencyResponse.query);

    forEach(latencyChartData, item => {
      const { id, data, names, customLabelComponent, colorIndex } = item;
      const areaData = [];
      const mutations = state.sharedMutations;
      const sourceMutation = mutations[id] || {};
      const metricsMutation = sourceMutation.metrics || {};
      const minMutation = metricsMutation[LATENCY_METRIC_MIN] || {};
      const maxMutation = metricsMutation[LATENCY_METRIC_MAX] || {};
      const highLight = !!minMutation.hover || !!maxMutation.hover;
      const show = (!!sourceMutation.show && (!!minMutation.show || !!maxMutation.show));
      if (!show) {
        return;
      }
      forEach(data, dataItem => {
        const { x, min: minVal, max: maxVal, date } = dataItem;
        let minLabel;
        let maxLabel;
        const dateTimeStr =
          getDisplayDate(moment.utc(date).toISOString(),
            timerange, timeAgg, startDate, endDate, 'tooltip');
        if (minVal) {
          minLabel = `${names.join(' > ')}\n\n${getI18nStrings('minLatencyText')} : ${minVal}ms\nDate and Time : \n${dateTimeStr}`;
        } else {
          minLabel = `${names.join(' > ')}\n\n${getI18nStrings('minLatencyText')} : ${getI18nStrings('noDataText')}\nDate and Time : \n${dateTimeStr}`;
        }
        if (maxVal) {
          maxLabel = `${names.join(' > ')}\n\n${getI18nStrings('maxLatencyText')} : ${maxVal}ms\nDate and Time : \n${dateTimeStr}`;
        } else {
          maxLabel = `${names.join(' > ')}\n\n${getI18nStrings('maxLatencyText')} : ${getI18nStrings('noDataText')}\nDate and Time : \n${dateTimeStr}`;
        }
        areaData.push({
          x,
          low: minVal,
          lowLabel: minLabel,
          high: maxVal,
          highLabel: maxLabel,
        });
      });
      areaCharts.push({
        colorIndex,
        customLabelComponent,
        data: areaData,
        highLight,
        id,
        type: Constants.CHART_ELEMENT_AREA,
        metric: `${LATENCY_METRIC_MIN}-${LATENCY_METRIC_MAX}`,
      });
    });
    return areaCharts;
  };

  const legendHoverHandler = (key, hoverState) => {
    setState(prevState => {
      let sharedMutations = prevState.sharedMutations || {};
      const source = getSourceFromMetricKey(key);
      const sourceMutation = (sharedMutations && sharedMutations[source]) || {};
      const metric = getMetricFromMetricKey(key);
      const metricsMutation = (sourceMutation && sourceMutation.metrics) || {};
      if (hoverState) {
        // DE437921: we started using hover state to enable/disable tooltip
        // But, with VictoryVoronoiContainer data points from different metrics
        // can be in hover state, when they have same values. so, i am
        // clearing hover state of all metrics to keep only the latest one
        // as highlighted(i.e. only it's tooltips will be active).
        // so user would see only 1 tooltip
        sharedMutations = clearMetricsHoverState(sharedMutations);
      }
      const metricMutation = (metricsMutation && metricsMutation[metric]) || {};
      const updatedMutations = {
        ...sharedMutations,
        [source]: {
          ...sourceMutation,
          metrics: {
            ...metricsMutation,
            [metric]: {
              ...metricMutation,
              hover: hoverState,
            },
          },
        },
      };
      return {
        sharedMutations: updatedMutations,
      };
    });
  };

  const legendClickHandler = (key, metricEvent) => {
    const {
      timerange,
      timeAgg,
      startDate,
      endDate,
    } = getParamsFromQueryData(defaultLatencyResponse && defaultLatencyResponse.query);
    // get dateRangeData passed, instead of calculating it
    const dateRangeData = getDateRangeData(
      timerange,
      timeAgg,
      startDate,
      endDate,
    );
    const latencyChartData = getLatencyChartData(
      dateRangeData,
      defaultLatencyResponse,
    );
    const colorIndex = latencyChartData.find(
      data => {
        const id = key.includes('___') ? key.substring(0, key.indexOf('___')) : key;
        return data.id === id;
      },
    ).colorIndex;
    setState(prevState => {
      const sharedMutations = prevState.sharedMutations || {};
      const sourceKey = metricEvent ? getSourceFromMetricKey(key) : key;
      const metricKey = getMetricFromMetricKey(key);
      const sourceMutation = (sharedMutations && sharedMutations[sourceKey]) || {};
      let updatedMutations;
      if (metricEvent) {
        const metric = getMetricFromMetricKey(key);
        const metricsMutation = (sourceMutation && sourceMutation.metrics) || {};
        const metricMutation = (metricsMutation && metricsMutation[metric]) || {};
        if ((metric === LATENCY_METRIC_MIN) || (metric === LATENCY_METRIC_MAX)) {
          let updatedMetrics = {};
          // don't change anything if source is hidden
          if (!sourceMutation.show) {
            return {};
          }
          updatedMetrics = {
            ...metricsMutation,
            [LATENCY_METRIC_MIN]: {
              ...metricsMutation[LATENCY_METRIC_MIN],
              show: !metricMutation.show,
            },
            [LATENCY_METRIC_MAX]: {
              ...metricsMutation[LATENCY_METRIC_MAX],
              show: !metricMutation.show,
            },
          };
          updatedMutations = {
            ...sharedMutations,
            [sourceKey]: {
              ...sourceMutation,
              metrics: updatedMetrics,
            },
          };
        } else if (metric === LATENCY_METRIC_CUSTOM_DATE || metric === LATENCY_METRIC_CUSTOM_WEEK) {
          updatedMutations = {
            ...sharedMutations,
            [sourceKey]: {
              show: true,
              metrics: {
                ...metricsMutation,
                avg: { show: true },
                [metricKey]: {
                  show: !metricMutation.show,
                },
              },
              colorIndex,
            },
          };
        } else {
          const show = !sourceMutation.show;
          updatedMutations = {
            ...sharedMutations,
            [sourceKey]: {
              ...sourceMutation,
              show,
              metrics: {
                [metricKey]: {
                  show: !sourceMutation.show,
                },
              },
            },
          };
        }
      }
      return {
        sharedMutations: updatedMutations,
        key, metricEvent,
      };
    });
  };

  const chartHoverHandler = (id, metric, hoverState) => {
    if (
      metric === LATENCY_METRIC_AVG ||
      metric === LATENCY_METRIC_CUSTOM_DATE ||
      metric === LATENCY_METRIC_CUSTOM_WEEK
    ) {
      legendHoverHandler(getMetricKey(id, metric), hoverState);
    } else {
      // hover behavior is same for min or max
      legendHoverHandler(getMetricKey(id, LATENCY_METRIC_MIN), hoverState);
    }
  }

  const createCustomLabelComponent = (styles) => {
    const StyledCustomLabelComponent = CustomLabelComponent(styles);
    return <StyledCustomLabelComponent />;
  };

  const getLatencyLegend = (color, isLine, show, pattern) => {
    const legendLineItemClass =
      pattern === 'dashed'
        ? classes.lineLegendItem__dashed
        : classes.lineLegendItem;
    return (<div
      className={
        isLine
          ? legendLineItemClass
          : classes.blockLegendItem
      }
      style={{
        backgroundColor: pattern === 'dashed' ? 'inherit' : color,
        visibility: show ? 'visible' : 'hidden',
        border: pattern === 'dashed' ? `2px dashed ${color}` : 'none',
      }}
    />);
  };

  const getLatencyLegendData = (
    latencyChartData,
    latencyDataFor30DaysAgo,
    latencyDataFor52WeeksAgo,
  ) => {
    const latencyLegendData = [];
    const { sharedMutations } = state;
    forEach(latencyChartData, (item, key) => {
      const individualData = [];
      const color = lineColors[key % lineColors.length];
      const sourceMutation = sharedMutations[item.id] || {};
      const sourceShow = !!sourceMutation.show;
      const metricsObj = (sourceMutation && sourceMutation.metrics) || {};
      const avgMetricKey = getMetricKey(item.id, LATENCY_METRIC_AVG);
      const maxMetricKey = getMetricKey(item.id, LATENCY_METRIC_MAX);
      const minMetricKey = getMetricKey(item.id, LATENCY_METRIC_MIN);
      const daysMetricKey = getMetricKey(item.id, LATENCY_METRIC_CUSTOM_DATE);
      const weeksMetricKey = getMetricKey(
        item.id,
        LATENCY_METRIC_CUSTOM_WEEK,
      );
      const selectedTimerange = defaultLatencyResponse.query.timerange;
      let totalLatenciesFor30DaysAgo = '';
      let totalLatenciesFor52WeeksAgo = '';
      if (selectedTimerange === 'hour') {
        totalLatenciesFor30DaysAgo = getI18nStrings('noDataText');
        totalLatenciesFor52WeeksAgo = getI18nStrings('noDataText');
      } else {
        if (is30DaysDataLoading) {
          totalLatenciesFor30DaysAgo = 'loading';
        } else {
          totalLatenciesFor30DaysAgo = latencyDataFor30DaysAgo &&
          latencyDataFor30DaysAgo.find(data => data.id === item.id) &&
          latencyDataFor30DaysAgo.find(data => data.id === item.id).avg;
        }
        if (is52WeeksDataLoading) {
          totalLatenciesFor52WeeksAgo = 'loading';
        } else {
          totalLatenciesFor52WeeksAgo = latencyDataFor52WeeksAgo &&
          latencyDataFor52WeeksAgo.find(data => data.id === item.id) &&
          latencyDataFor52WeeksAgo.find(data => data.id === item.id).avg;
        }
        if (latencyResponseFor30DaysAgo && !totalLatenciesFor30DaysAgo) {
          totalLatenciesFor30DaysAgo = getI18nStrings('noDataText');
        }
        if (latencyResponseFor52WeeksAgo && !totalLatenciesFor52WeeksAgo) {
          totalLatenciesFor52WeeksAgo = getI18nStrings('noDataText');
        }
      }
      const daysMetricShow = !!(
        sourceShow &&
        metricsObj[LATENCY_METRIC_CUSTOM_DATE] &&
        sourceShow &&
        metricsObj[LATENCY_METRIC_CUSTOM_DATE].show
      );
      const weeksMetricShow = !!(
        sourceShow &&
        metricsObj[LATENCY_METRIC_CUSTOM_WEEK] &&
        sourceShow &&
        metricsObj[LATENCY_METRIC_CUSTOM_WEEK].show
      );
      const lightShadeColor =
        lightShadeLineColors[key % lightShadeLineColors.length];
      const maxMetricShow = !!(
        sourceShow &&
        metricsObj[LATENCY_METRIC_MAX] &&
        sourceShow &&
        metricsObj[LATENCY_METRIC_MAX].show
      );
      const minMetricShow = !!(
        sourceShow &&
        metricsObj[LATENCY_METRIC_MIN] &&
        sourceShow &&
        metricsObj[LATENCY_METRIC_MIN].show
      );
      individualData.push(
        {
          component: getLatencyLegend(color, true, sourceShow),
          id: avgMetricKey,
          label: 'label.analytics.latencyChart.apiLatency.text',
          show: sourceShow,
          value: item[LATENCY_METRIC_AVG],
        },
        {
          component: getLatencyLegend(color, false, maxMetricShow),
          id: maxMetricKey,
          label: 'label.analytics.latencyChart.apiLatency.max.text',
          show: maxMetricShow,
          value: item[LATENCY_METRIC_MAX],
        },
        {
          component: getLatencyLegend(color, false, minMetricShow),
          id: minMetricKey,
          label: 'label.analytics.latencyChart.apiLatency.min.text',
          show: minMetricShow,
          value: item[LATENCY_METRIC_MIN],
        },
        {
          id: daysMetricKey,
          label: 'label.analytics.latencyChart.legend.30daysLabel.text',
          value: (daysMetricShow && totalLatenciesFor30DaysAgo) || '',
          component: getLatencyLegend(color, true, daysMetricShow, 'dashed'),
          show: daysMetricShow,
          disable: selectedTimerange === 'hour',
        },
        {
          id: weeksMetricKey,
          label: 'label.analytics.latencyChart.legend.52weeksLabel.text',
          value: (weeksMetricShow && totalLatenciesFor52WeeksAgo) || '',
          component: getLatencyLegend(lightShadeColor, true, weeksMetricShow),
          show: weeksMetricShow,
          disable: selectedTimerange === 'hour',
        },
      );
      latencyLegendData.push({
        uuid: item.id,
        names: item.names,
        show: sourceShow,
        color,
        data: individualData,
        suffix: 'ms',
      });
    });
    return latencyLegendData;
  };

  const {
    timerange,
    timeAgg,
    startDate,
    endDate,
  } = getParamsFromQueryData(defaultLatencyResponse && defaultLatencyResponse.query);
  const yAxisLabel = getI18nStrings('yAxisLabel');
  const chartScale = { x: 'time' };

  const dateRangeData = getDateRangeData(timerange, timeAgg, startDate, endDate);
  const latencyChartData = getLatencyChartData(dateRangeData, defaultLatencyResponse);
  const lineChartsData = getLineChartsData(latencyChartData, LATENCY_METRIC_AVG) || [];
  const areaChartsData = getAreaChartsData(latencyChartData) || [];
  let latencyLegendData = getLatencyLegendData(latencyChartData);
  const historicalChartDataFor30Days = [];
  const historicalChartDataFor52Weeks = [];
  if (latencyResponseFor30DaysAgo) {
    historicalChartDataFor30Days.push(...getHistoricalChartDataFromResponse(
      latencyResponseFor30DaysAgo),
    );
    const lineChartDataFor30Days = getLineChartsData(
      historicalChartDataFor30Days,
      LATENCY_METRIC_CUSTOM_DATE,
    );
    lineChartsData.push(...lineChartDataFor30Days);
  }
  if (latencyResponseFor52WeeksAgo) {
    historicalChartDataFor52Weeks.push(...getHistoricalChartDataFromResponse(
      latencyResponseFor52WeeksAgo),
    );
    const lineChartDataFor52Weeks = getLineChartsData(
      historicalChartDataFor52Weeks,
      LATENCY_METRIC_CUSTOM_WEEK,
    );
    lineChartsData.push(...lineChartDataFor52Weeks);
  }
  if (historicalChartDataFor52Weeks.length || historicalChartDataFor30Days.length) {
    latencyLegendData = getLatencyLegendData(
      latencyChartData,
      historicalChartDataFor30Days,
      historicalChartDataFor52Weeks,
    );
  }
  const renderChartComponent = () =>
    (isLoading ? (
      <div className={classes.loader}>
        <Loading />
      </div>
    ) : (
      <div className={classes.chartContainer}>
        <div className={classes.lineChartContainer}>
          <div className={classes.plcaeholderText}>
            {intl.getI18nMessage('label.analytics.latencyChart.placeholder.title.text')}
          </div>
          <CompositeXYChart
            chartData={[...areaChartsData, ...lineChartsData]}
            chartScale={chartScale}
            formatXAxisData={(item) => getXAxisFormatter(item, dateRangeData)}
            lineColors={lineColors}
            xAxisLabel={getI18nStrings('dateTimeFormat')}
            xAxisValues={dateRangeData.xAxisTickValues}
            yAxisLabel={yAxisLabel}
            lightShadeLineColors={lightShadeLineColors}
            hoverHandler={chartHoverHandler}
          />
        </div>
        <div className={clsx(classes.labelForPrint, classes.latencyChartLabel)}>
          {intl.getI18nMessage('label.analytics.lineChart.metric.text')}
        </div>
        <ChartLegend
          data={latencyLegendData}
          hoverHandler={legendHoverHandler}
          clickHandler={legendClickHandler}
        />
      </div>
    )
  );
  return (
    <div
      className={classes.apiLatencyChartContainer}
      data-apim-test="chart-latency"
    >
      <div className={classes.latencyChartLabel}>
        <div className={classes.chartTitle}>
          {intl.getI18nMessage('label.analytics.latency.chart.title.text')}
        </div>
        <div className={classes.metricsLabel}>
          {intl.getI18nMessage('label.analytics.lineChart.metric.text')}
        </div>
      </div>
      {!latencyChartData.length && !isLoading ? <NoData /> : renderChartComponent()}
    </div>
  );
  
}

LatencyChart.propTypes = {
  defaultLatencyResponse: PropTypes.array,
  apiMap: PropTypes.shape(),
  appMap: PropTypes.shape(),
  orgMap: PropTypes.shape(),
  isLoading: PropTypes.bool,
  fetchCustomDateMetricsData: PropTypes.func,
  latencyResponseFor30DaysAgo: PropTypes.object,
  latencyResponseFor52WeeksAgo: PropTypes.object,
  is30DaysDataLoading: PropTypes.bool,
  is52WeeksDataLoading: PropTypes.bool,
};

export default LatencyChart;